import { useState, useEffect, useMemo, Fragment, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HardHat,
  Plus,
  Search,
  RotateCcw,
  Edit2,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Building2,
  Wrench,
  Layers,
  Filter,
  Users,
  ChevronDown,
  ChevronUp,
  Briefcase,
  IndianRupee,
  ExternalLink,
} from 'lucide-react';
import { formatMoney, toPaise } from '@vns/core';
import {
  useSubcontractors,
  useCreateSubcontractor,
  useCreateWorkType,
  useSubcontractorContracts,
  useCreateSubcontractorContract,
  useUpdateSubcontractorContract,
  useDeleteSubcontractorContract,
  type NewSubcontractor,
  type SubcontractorRow,
  type SubcontractorContractRow,
} from '../lib/directory';
import { useUpdateSubcontractor, useDeleteSubcontractor } from '../lib/directory-edit';
import { useLookups } from '../lib/lookups';
import { supabase } from '../lib/supabase';
import Shell from '../components/Shell';
import MultiSelect from '../components/MultiSelect';
import Confirm, { useConfirm } from '../components/Confirm';
import { useRestore } from '../lib/restore';
import { guardSubcontractor } from '../lib/delete-guards';
import RowActions from '../components/RowActions';
import { SectionTitle, BankDetailsAccordion } from './Suppliers';
import {
  Drawer,
  Field,
  EmptyState,
  TableSkeleton,
  ErrorState,
  initials,
  useToast,
  Select,
} from '../components/ui';

export default function Subcontractors() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [workType, setWorkType] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<SubcontractorRow | null>(null);
  const [expandedSubIds, setExpandedSubIds] = useState<Set<string>>(new Set());
  const [addingContractSub, setAddingContractSub] = useState<SubcontractorRow | null>(null);
  const [editingContract, setEditingContract] = useState<SubcontractorContractRow | null>(null);

  const confirm = useConfirm<SubcontractorRow>();
  const confirmContract = useConfirm<SubcontractorContractRow>();
  const restore = useRestore();
  const remove = useDeleteSubcontractor();
  const removeContract = useDeleteSubcontractorContract();
  const toast = useToast();
  const { data: lk } = useLookups();
  const { data: allRows, isLoading, error, refetch } = useSubcontractors(search);
  const { data: allContracts } = useSubcontractorContracts();

  function toggleExpand(subId: string) {
    setExpandedSubIds((prev) => {
      const next = new Set(prev);
      if (next.has(subId)) {
        next.delete(subId);
      } else {
        next.add(subId);
      }
      return next;
    });
  }

  // Filter by selected work type
  const data = allRows?.filter((s) => !workType || s.workTypes.includes(workType));
  const filtered = !!search || !!workType;
  const clearFilters = () => {
    setSearch('');
    setWorkType('');
  };

  async function doDelete() {
    if (!confirm.target) return;
    try {
      await remove.mutateAsync(confirm.target.id);
      const removed = confirm.target;
      toast.showWithAction(removed.name + ' removed', {
        label: 'Undo',
        run: () => restore.mutateAsync({ table: 'subcontractors', id: removed.id }),
      });
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not remove the sub-contractor', true);
    } finally {
      confirm.close();
    }
  }

  async function doDeleteContract() {
    if (!confirmContract.target) return;
    try {
      await removeContract.mutateAsync(confirmContract.target.id);
      const removed = confirmContract.target;
      toast.showWithAction(`${removed.projectName} contract removed`, {
        label: 'Undo',
        run: () => restore.mutateAsync({ table: 'subcontractor_contracts', id: removed.id }),
      });
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not remove project contract', true);
    } finally {
      confirmContract.close();
    }
  }

  // Summary Metrics
  const totalSubcontractors = data?.length ?? 0;
  const totalTrades = new Set(data?.flatMap((s) => s.workTypes) ?? []).size;
  const uniqueCities = new Set(data?.map((s) => s.city).filter(Boolean)).size;
  const totalAssignedProjects = allContracts?.length ?? 0;

  return (
    <Shell
      title="Sub Contractors"
      crumb="Directory"
      actions={
        <button
          className="btn btn-primary btn-auto btn-sm"
          onClick={() => setAdding(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-sm)' }}
        >
          <Plus size={15} strokeWidth={2.2} />
          <span>Add Sub Contractor</span>
        </button>
      }
    >
      {/* Quick Summary KPIs */}
      {!isLoading && data && (
        <motion.div
          className="kpi-row"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          style={{ marginBottom: 18 }}
        >
          <div className="kpi">
            <div className="lbl">
              <span>Total Sub-Contractors</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <HardHat size={14} />
              </div>
            </div>
            <div className="val">{totalSubcontractors}</div>
            <div className="sub">labour contractor partners</div>
          </div>

          <div className="kpi">
            <div className="lbl">
              <span>Active Trade Specialisations</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#ecfdf5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Wrench size={14} />
              </div>
            </div>
            <div className="val" style={{ color: '#059669' }}>{totalTrades}</div>
            <div className="sub">skilled construction trades</div>
          </div>

          <div className="kpi">
            <div className="lbl">
              <span>Assigned Project Sites</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#e0e7ff', color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Building2 size={14} />
              </div>
            </div>
            <div className="val" style={{ color: '#4338ca' }}>{totalAssignedProjects}</div>
            <div className="sub">sub-contractor project contracts</div>
          </div>

          <div className="kpi">
            <div className="lbl">
              <span>Contractor Locations</span>
              <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-xs)', background: '#f5f3ff', color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MapPin size={14} />
              </div>
            </div>
            <div className="val">{uniqueCities}</div>
            <div className="sub">cities across workforce</div>
          </div>
        </motion.div>
      )}

      {/* Modern Filter Toolbar */}
      <div
        className="toolbar"
        style={{
          background: 'var(--surface)',
          padding: '10px 14px',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-xs)',
          marginBottom: 16,
        }}
      >
        <div className="field-inline" style={{ minWidth: 260, flex: 1 }}>
          <Search size={14} style={{ color: 'var(--text-faint)' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sub-contractors by name, phone, trade, city..."
            aria-label="Search sub-contractors"
          />
        </div>

        <div style={{ minWidth: 180 }}>
          <Select
            value={workType}
            onChange={(val) => setWorkType(val)}
            placeholder="All Work Types"
            options={[
              { value: '', label: 'All Work Types' },
              ...(lk?.workTypes.map((w) => ({
                value: w.name,
                label: w.name,
              })) ?? []),
            ]}
          />
        </div>

        {filtered && (
          <button
            className="btn btn-auto btn-sm btn-ghost"
            onClick={clearFilters}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <RotateCcw size={13} />
            <span>Clear</span>
          </button>
        )}
      </div>

      {/* Subcontractors Table Card */}
      <motion.div
        className="card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="table-wrap">
          {isLoading ? (
            <TableSkeleton rows={5} cols={7} />
          ) : error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : !data?.length ? (
            <EmptyState
              icon="hardhat"
              title={filtered ? 'No sub-contractors match this filter' : 'No sub-contractors yet'}
              hint={filtered ? 'Try clearing the filters above.' : 'Add one so you can record daily labour against them.'}
            />
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', paddingLeft: 24 }}>Contractor Profile</th>
                  <th style={{ textAlign: 'left' }}>Work Types / Trades</th>
                  <th style={{ textAlign: 'left' }}>Contact Phone</th>
                  <th style={{ textAlign: 'left' }}>Location</th>
                  <th style={{ textAlign: 'left' }}>GST Number</th>
                  <th style={{ textAlign: 'left' }}>Projects</th>
                  <th style={{ textAlign: 'right', paddingRight: 24 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((s) => {
                  const subContracts = (allContracts ?? []).filter((c) => c.subcontractorId === s.id);
                  const isExpanded = expandedSubIds.has(s.id);

                  return (
                    <Fragment key={s.id}>
                      <tr
                        className={`clickable ${isExpanded ? 'expanded-row' : ''}`}
                        onClick={() => toggleExpand(s.id)}
                        style={{
                          cursor: 'pointer',
                          background: isExpanded ? 'var(--surface-2)' : undefined,
                          borderBottom: isExpanded ? 'none' : undefined,
                        }}
                      >
                        <td style={{ textAlign: 'left', paddingLeft: 24 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12 }}>
                            <div
                              className="avatar-sq"
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 'var(--radius-xs)',
                                background: '#eff6ff',
                                color: '#2563eb',
                                fontWeight: 700,
                                fontSize: 12,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {initials(s.name)}
                            </div>
                            <div style={{ textAlign: 'left' }}>
                              <div className="name" style={{ fontWeight: 600, color: 'var(--text)' }}>
                                {s.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'left', whiteSpace: 'normal', maxWidth: 300 }}>
                          {s.workTypes.length ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-start' }}>
                              {s.workTypes.map((t) => (
                                <span
                                  key={t}
                                  style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-xs)',
                                    fontSize: 11.5,
                                    fontWeight: 500,
                                    background: 'var(--surface-2)',
                                    color: 'var(--text-soft)',
                                    border: '1px solid var(--border-subtle)',
                                  }}
                                >
                                  {t}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--text-faint)' }}>—</span>
                          )}
                        </td>
                        <td className="num" style={{ textAlign: 'left', fontWeight: 500 }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-soft)' }}>
                            <Phone size={12} style={{ color: 'var(--text-faint)' }} />
                            <span>{s.phone}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'left' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-soft)' }}>
                            <MapPin size={12} style={{ color: 'var(--text-faint)' }} />
                            <span>{s.city ?? '—'}</span>
                          </div>
                        </td>
                        <td className="num" style={{ textAlign: 'left', color: 'var(--text-soft)', fontSize: 12 }}>
                          {s.gst_number ? (
                            <span style={{ fontFamily: 'var(--font-mono)', background: 'var(--surface-2)', padding: '2px 6px', borderRadius: 'var(--radius-xs)', border: '1px solid var(--border-subtle)' }}>
                              {s.gst_number}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="num" style={{ textAlign: 'left' }}>
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '3px 10px',
                              borderRadius: 'var(--radius-xs)',
                              fontSize: 12,
                              fontWeight: 600,
                              background: isExpanded
                                ? '#dbeafe'
                                : subContracts.length > 0
                                ? '#eff6ff'
                                : '#f8fafc',
                              color: isExpanded
                                ? '#1d4ed8'
                                : subContracts.length > 0
                                ? '#2563eb'
                                : 'var(--text-faint)',
                              border: `1px solid ${
                                isExpanded
                                  ? '#93c5fd'
                                  : subContracts.length > 0
                                  ? '#dbeafe'
                                  : 'var(--border)'
                              }`,
                              cursor: 'pointer',
                              userSelect: 'none',
                            }}
                            title={isExpanded ? 'Click to collapse project list' : 'Click to expand project list'}
                          >
                            <span>
                              {subContracts.length} {subContracts.length === 1 ? 'Project' : 'Projects'}
                            </span>
                            {isExpanded ? (
                              <ChevronUp size={13} strokeWidth={2.4} />
                            ) : (
                              <ChevronDown size={13} strokeWidth={2.4} />
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', paddingRight: 24 }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                            <button
                              className="icon-btn"
                              onClick={() => setEditing(s)}
                              title={`Edit ${s.name}`}
                              aria-label={`Edit ${s.name}`}
                              style={{ width: 28, height: 28 }}
                            >
                              <Edit2 size={13} />
                            </button>
                            <button
                              className="icon-btn btn-danger"
                              onClick={() => confirm.ask(s)}
                              title={`Delete ${s.name}`}
                              aria-label={`Delete ${s.name}`}
                              style={{ width: 28, height: 28 }}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Dropdown Projects Detail Table for this Sub Contractor */}
                      {isExpanded && (
                        <tr className="nested-projects-row" style={{ background: 'var(--surface-2)' }}>
                          <td colSpan={7} style={{ padding: '0 0 16px 0', borderBottom: '2px solid var(--border)' }}>
                            <div
                              style={{
                                margin: '6px 20px 8px 24px',
                                background: '#ffffff',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--radius)',
                                boxShadow: 'var(--shadow-xs)',
                                overflow: 'hidden',
                              }}
                            >
                              {/* Nested Header */}
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '12px 18px',
                                  background: 'var(--surface-2)',
                                  borderBottom: '1px solid var(--border)',
                                  flexWrap: 'wrap',
                                  gap: 10,
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <Building2 size={16} style={{ color: 'var(--accent)' }} />
                                  <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>
                                    Projects Detail — {s.name}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      padding: '2px 8px',
                                      borderRadius: 10,
                                      background: subContracts.length > 0 ? '#dbeafe' : 'var(--surface-3)',
                                      color: subContracts.length > 0 ? '#1d4ed8' : 'var(--text-soft)',
                                    }}
                                  >
                                    {subContracts.length} {subContracts.length === 1 ? 'Site Assigned' : 'Sites Assigned'}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  className="btn btn-primary btn-auto btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAddingContractSub(s);
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 12,
                                    padding: '5px 12px',
                                    borderRadius: 'var(--radius-sm)',
                                  }}
                                >
                                  <Plus size={13} strokeWidth={2.4} />
                                  <span>Add Project for this Sub Contractor</span>
                                </button>
                              </div>

                              {/* Nested Table or Empty */}
                              {subContracts.length === 0 ? (
                                <div style={{ padding: '26px 20px', textAlign: 'center' }}>
                                  <p style={{ fontSize: 13, color: 'var(--text-soft)', margin: '0 0 12px' }}>
                                    No projects assigned to this sub-contractor yet.
                                  </p>
                                  <button
                                    type="button"
                                    className="btn btn-auto btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAddingContractSub(s);
                                    }}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                  >
                                    <Plus size={13} />
                                    <span>Assign First Project</span>
                                  </button>
                                </div>
                              ) : (
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ margin: 0, width: '100%', fontSize: 12.5 }}>
                                    <thead>
                                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)' }}>
                                        <th style={{ textAlign: 'left', padding: '10px 16px', fontWeight: 600, color: 'var(--text-muted)' }}>Project Name</th>
                                        <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--text-muted)' }}>Client</th>
                                        <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600, color: 'var(--text-muted)' }}>Contractor Total Amount</th>
                                        <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600, color: 'var(--text-muted)' }}>Paid Amount</th>
                                        <th style={{ textAlign: 'right', padding: '10px 14px', fontWeight: 600, color: 'var(--text-muted)' }}>Pending Balance</th>
                                        <th style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, color: 'var(--text-muted)' }}>Remarks</th>
                                        <th style={{ textAlign: 'right', padding: '10px 16px', fontWeight: 600, color: 'var(--text-muted)' }}>Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {subContracts.map((c) => (
                                        <tr
                                          key={c.id}
                                          className="clickable"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/projects/${c.projectId}`);
                                          }}
                                          style={{
                                            cursor: 'pointer',
                                            transition: 'background 0.15s ease',
                                            borderBottom: '1px solid var(--border-subtle)',
                                          }}
                                        >
                                          <td style={{ padding: '11px 16px', fontWeight: 600, color: 'var(--text)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                              <div
                                                style={{
                                                  width: 26,
                                                  height: 26,
                                                  borderRadius: 'var(--radius-xs)',
                                                  background: 'var(--surface-2)',
                                                  color: 'var(--text-soft)',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  flexShrink: 0,
                                                }}
                                              >
                                                <Building2 size={13} />
                                              </div>
                                              <span style={{ color: 'var(--text)', fontWeight: 600 }}>{c.projectName}</span>
                                            </div>
                                          </td>
                                          <td style={{ padding: '11px 14px', color: 'var(--text-soft)' }}>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                              <Users size={11} style={{ color: 'var(--text-faint)' }} />
                                              <span>{c.clientName || '—'}</span>
                                            </div>
                                          </td>
                                          <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>
                                            {formatMoney(c.contractAmountPaise)}
                                          </td>
                                          <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: 600, color: '#059669', fontFamily: 'var(--font-mono)' }}>
                                            {formatMoney(c.paidAmountPaise)}
                                          </td>
                                          <td style={{ padding: '11px 14px', textAlign: 'right', fontWeight: 600, color: c.pendingAmountPaise > 0 ? '#d97706' : 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>
                                            {formatMoney(c.pendingAmountPaise)}
                                          </td>
                                          <td style={{ padding: '11px 14px', color: 'var(--text-soft)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {c.remarks || '—'}
                                          </td>
                                          <td style={{ padding: '11px 16px', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                                              <button
                                                className="icon-btn"
                                                onClick={() => {
                                                  setEditingContract(c);
                                                  setAddingContractSub(s);
                                                }}
                                                title={`Edit Contract for ${c.projectName}`}
                                                aria-label={`Edit Contract for ${c.projectName}`}
                                                style={{ width: 26, height: 26 }}
                                              >
                                                <Edit2 size={12} />
                                              </button>
                                              <button
                                                className="icon-btn btn-danger"
                                                onClick={() => confirmContract.ask(c)}
                                                title={`Delete Contract for ${c.projectName}`}
                                                aria-label={`Delete Contract for ${c.projectName}`}
                                                style={{ width: 26, height: 26 }}
                                              >
                                                <Trash2 size={12} />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>

      {adding && <SubcontractorDrawer onClose={() => setAdding(false)} />}
      {editing && <SubcontractorDrawer existing={editing} onClose={() => setEditing(null)} />}

      {addingContractSub && (
        <SubcontractorContractDrawer
          subcontractor={addingContractSub}
          existing={editingContract}
          onClose={() => {
            setAddingContractSub(null);
            setEditingContract(null);
          }}
        />
      )}

      {confirm.target && (
        <Confirm
          title={'Delete ' + confirm.target.name + '?'}
          message="They will disappear from your sub-contractor list. You can undo this straight away from the notification message that appears."
          check={guardSubcontractor(confirm.target.id)}
          busy={remove.isPending}
          onConfirm={() => void doDelete()}
          onCancel={confirm.close}
        />
      )}

      {confirmContract.target && (
        <Confirm
          title={`Remove project contract for ${confirmContract.target.projectName}?`}
          message="This will remove the agreed project contract value for this sub-contractor. Any recorded attendance and payment history remain intact."
          busy={removeContract.isPending}
          onConfirm={() => void doDeleteContract()}
          onCancel={confirmContract.close}
        />
      )}
    </Shell>
  );
}

const EMPTY: NewSubcontractor = {
  name: '', phone: '', email: '',
  address: '', city: '', gst_number: '', remarks: '', workTypeIds: [],
  bank_name: '', bank_holder: '', bank_account_no: '', bank_ifsc: '', bank_branch: '',
};

/** One drawer for both Add & Edit Subcontractor */
export function SubcontractorDrawer({ existing, onClose }: { existing?: SubcontractorRow; onClose: () => void }) {
  const { data: lk } = useLookups();
  const [form, setForm] = useState<NewSubcontractor>(
    existing
      ? {
          ...EMPTY,
          name: existing.name,
          phone: existing.phone,
          city: existing.city ?? '',
          gst_number: existing.gst_number ?? '',
          workTypeIds: [],
        }
      : EMPTY,
  );
  const [resolved, setResolved] = useState(!existing);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useCreateSubcontractor();
  const update = useUpdateSubcontractor();
  const createWorkType = useCreateWorkType();
  const createContract = useCreateSubcontractorContract();
  const toast = useToast();

  // Optional Project Assignment states
  const [projClientId, setProjClientId] = useState('');
  const [projProjectId, setProjProjectId] = useState('');
  const [projContractAmount, setProjContractAmount] = useState('');
  const [projRemarks, setProjRemarks] = useState('');

  const filteredProjects = useMemo(() => {
    if (!projClientId) return lk?.projects ?? [];
    return (lk?.projects ?? []).filter((p) => p.client_id === projClientId);
  }, [lk?.projects, projClientId]);

  const handleProjClientChange = (cId: string) => {
    setProjClientId(cId);
    if (cId && projProjectId) {
      const proj = lk?.projects?.find((p) => p.id === projProjectId);
      if (proj && proj.client_id !== cId) {
        setProjProjectId('');
      }
    }
  };

  const handleProjProjectChange = (pId: string) => {
    setProjProjectId(pId);
    const proj = lk?.projects?.find((p) => p.id === pId);
    if (proj?.client_id && !projClientId) {
      setProjClientId(proj.client_id);
    }
  };

  const busy = create.isPending || update.isPending || createContract.isPending;

  useEffect(() => {
    if (!existing?.id) return;
    let cancel = false;
    supabase
      .from('subcontractors')
      .select('email, address, city, gst_number, remarks, bank_name, bank_holder, bank_account_no, bank_ifsc, bank_branch')
      .eq('id', existing.id)
      .single()
      .then(({ data }) => {
        if (cancel || !data) return;
        setForm((f) => ({
          ...f,
          email: data.email ?? f.email,
          address: data.address ?? f.address,
          remarks: data.remarks ?? f.remarks,
          bank_name: data.bank_name ?? f.bank_name,
          bank_holder: data.bank_holder ?? f.bank_holder,
          bank_account_no: data.bank_account_no ?? f.bank_account_no,
          bank_ifsc: data.bank_ifsc ?? f.bank_ifsc,
          bank_branch: data.bank_branch ?? f.bank_branch,
        }));
      });
    return () => {
      cancel = true;
    };
  }, [existing?.id]);

  // Resolve trade names to ids
  if (existing && !resolved && lk?.workTypes.length) {
    const ids = lk.workTypes.filter((w) => existing.workTypes.includes(w.name)).map((w) => w.id);
    setForm((f) => ({ ...f, workTypeIds: ids }));
    setResolved(true);
  }

  const set = (k: keyof NewSubcontractor) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Contractor name is required';
    if (!form.phone.trim()) next.phone = 'Phone number is required';
    else if (form.phone.replace(/\D/g, '').length < 10) next.phone = 'Enter a valid phone number';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email';
    if (!form.workTypeIds.length) next.workTypeIds = 'Choose at least one work type';

    // If project is chosen, contractor total amount is required for that contract
    if (projProjectId && (!projContractAmount.trim() || parseFloat(projContractAmount) <= 0)) {
      next.projContractAmount = 'Enter contractor total amount for this project';
    }

    setErrors(next);
    if (Object.keys(next).length) return;

    try {
      let subId = existing?.id;
      if (existing) {
        await update.mutateAsync({ ...form, id: existing.id });
        toast.show(`${form.name} updated`);
      } else {
        const created = await create.mutateAsync(form);
        subId = created.id;
        toast.show(`${form.name} added`);
      }

      // If user filled project assignment (optional)
      if (subId && projProjectId && projContractAmount.trim()) {
        try {
          await createContract.mutateAsync({
            subcontractorId: subId,
            projectId: projProjectId,
            contractAmountPaise: toPaise(projContractAmount),
            remarks: projRemarks,
          });
          toast.show(`Project assigned with total amount ₹${parseFloat(projContractAmount).toLocaleString('en-IN')}`);
        } catch (contractErr) {
          toast.show(contractErr instanceof Error ? contractErr.message : 'Could not assign project', true);
        }
      }

      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the sub-contractor', true);
    }
  }

  return (
    <Drawer
      title={existing ? 'Edit Sub Contractor' : 'Add Sub Contractor'}
      description={existing ? existing.name : 'Register a new labour sub-contractor'}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={existing ? 'Save Changes' : 'Create Sub Contractor'}
      busy={busy}
    >
      <div className="form-grid">
        <Field label="Contractor Name" error={errors.name} span2>
          <input
            value={form.name}
            onChange={set('name')}
            autoFocus
            aria-invalid={!!errors.name}
          />
        </Field>

        <Field label="Primary Phone" error={errors.phone}>
          <input
            value={form.phone}
            onChange={set('phone')}
            inputMode="tel"
            aria-invalid={!!errors.phone}
          />
        </Field>

        <Field label="Location (City)">
          <input
            value={form.city}
            onChange={set('city')}
          />
        </Field>

        <Field label="Email ID" error={errors.email} optional>
          <input
            type="email"
            value={form.email}
            onChange={set('email')}
            aria-invalid={!!errors.email}
          />
        </Field>

        <Field label="GSTIN Number" optional>
          <input
            value={form.gst_number}
            onChange={set('gst_number')}
            style={{ textTransform: 'uppercase' }}
          />
        </Field>

        <Field label="Address / Base Office" optional span2>
          <textarea
            value={form.address}
            onChange={set('address')}
          />
        </Field>
      </div>

      <SectionTitle>Specialisation / Work Types</SectionTitle>
      {errors.workTypeIds && (
        <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 8, fontWeight: 500 }}>
          {errors.workTypeIds}
        </div>
      )}
      <MultiSelect
        options={lk?.workTypes}
        selected={form.workTypeIds}
        onChange={(ids) => setForm((f) => ({ ...f, workTypeIds: ids }))}
        collapseAfter={9}
        onCreateOption={(name) => createWorkType.mutateAsync(name)}
        customLabel="Work Type / Trade"
      />

      {/* Project Assignment Section (Optional) */}
      <SectionTitle>Assign Project (Optional)</SectionTitle>
      <div className="form-grid">
        <Field label="Select Client" optional>
          <Select
            value={projClientId}
            onChange={handleProjClientChange}
            placeholder="All Clients / Filter Projects..."
            options={[
              { value: '', label: 'All Clients' },
              ...(lk?.clients?.map((c) => ({ value: c.id, label: c.name })) ?? []),
            ]}
          />
        </Field>

        <Field label="Select Project" optional>
          <Select
            value={projProjectId}
            onChange={handleProjProjectChange}
            placeholder="Choose project..."
            options={[
              { value: '', label: 'None / Assign Later' },
              ...filteredProjects.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
        </Field>

        {projProjectId && (
          <>
            <Field
              label="Contractor Total Amount (₹)"
              error={errors.projContractAmount}
              hint="Fixed agreed total amount for this contractor on this project"
              span2
            >
              <input
                type="number"
                step="any"
                value={projContractAmount}
                onChange={(e) => setProjContractAmount(e.target.value)}
                style={{ fontWeight: 700, fontSize: 15, fontFamily: 'var(--font-mono)' }}
              />
            </Field>

            <Field label="Project Remarks / Terms" optional span2>
              <input
                value={projRemarks}
                onChange={(e) => setProjRemarks(e.target.value)}
              />
            </Field>
          </>
        )}
      </div>

      <BankDetailsAccordion values={form} onChange={set} />

      <SectionTitle>Additional Notes</SectionTitle>
      <div className="form-grid">
        <Field label="Remarks & Terms" optional span2>
          <input
            value={form.remarks}
            onChange={set('remarks')}
          />
        </Field>
      </div>
    </Drawer>
  );
}

/** Drawer to Add or Edit Project Contract for a Subcontractor */
export function SubcontractorContractDrawer({
  subcontractor,
  existing,
  onClose,
}: {
  subcontractor: { id: string; name: string };
  existing?: SubcontractorContractRow | null;
  onClose: () => void;
}) {
  const { data: lk } = useLookups();
  const create = useCreateSubcontractorContract();
  const update = useUpdateSubcontractorContract();
  const toast = useToast();

  const [clientId, setClientId] = useState(existing?.clientId || '');
  const [projectId, setProjectId] = useState(existing?.projectId || '');
  const [amountRupees, setAmountRupees] = useState(
    existing ? (existing.contractAmountPaise / 100).toString() : '',
  );
  const [remarks, setRemarks] = useState(existing?.remarks || '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const filteredProjects = useMemo(() => {
    if (!clientId) return lk?.projects ?? [];
    return (lk?.projects ?? []).filter((p) => p.client_id === clientId);
  }, [lk?.projects, clientId]);

  const handleClientChange = (cId: string) => {
    setClientId(cId);
    if (cId && projectId) {
      const proj = lk?.projects?.find((p) => p.id === projectId);
      if (proj && proj.client_id !== cId) {
        setProjectId('');
      }
    }
  };

  const handleProjectChange = (pId: string) => {
    setProjectId(pId);
    const proj = lk?.projects?.find((p) => p.id === pId);
    if (proj?.client_id && !clientId) {
      setClientId(proj.client_id);
    }
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!projectId) next.projectId = 'Please select a project';
    if (!amountRupees.trim() || parseFloat(amountRupees) <= 0) {
      next.amountRupees = 'Enter a valid contractor total amount';
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    try {
      if (existing) {
        await update.mutateAsync({
          id: existing.id,
          projectId,
          contractAmountPaise: toPaise(amountRupees),
          remarks,
        });
        toast.show(`Project contract for ${subcontractor.name} updated`);
      } else {
        await create.mutateAsync({
          subcontractorId: subcontractor.id,
          projectId,
          contractAmountPaise: toPaise(amountRupees),
          remarks,
        });
        toast.show(`Project assigned to ${subcontractor.name}`);
      }
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save project contract', true);
    }
  }

  return (
    <Drawer
      title={existing ? 'Edit Project Contract' : 'Assign Project to Sub Contractor'}
      description={`Sub Contractor: ${subcontractor.name}`}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={existing ? 'Save Contract' : 'Assign Project'}
      busy={create.isPending || update.isPending}
    >
      <div className="form-grid">
        <Field label="Select Client" optional>
          <Select
            value={clientId}
            onChange={handleClientChange}
            placeholder="All Clients / Filter Projects..."
            options={[
              { value: '', label: 'All Clients' },
              ...(lk?.clients?.map((c) => ({ value: c.id, label: c.name })) ?? []),
            ]}
          />
        </Field>

        <Field label="Select Project" error={errors.projectId}>
          <Select
            value={projectId}
            onChange={handleProjectChange}
            placeholder="Choose project..."
            options={[
              { value: '', label: 'Choose project...' },
              ...filteredProjects.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
        </Field>

        <Field
          label="Contractor Total Amount (₹)"
          error={errors.amountRupees}
          hint="Agreed fixed total amount for this contractor on this project"
          span2
        >
          <input
            type="number"
            step="any"
            value={amountRupees}
            onChange={(e) => setAmountRupees(e.target.value)}
            required
            style={{ fontWeight: 700, fontSize: 15, fontFamily: 'var(--font-mono)' }}
          />
        </Field>

        <Field label="Remarks & Terms" optional span2>
          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}

